import { getAuthToken } from "./authApi";

export const PRODUCTS_API_URL = "https://uktomars.co.uk/api/products";
export const CATEGORIES_API_URL = "https://uktomars.co.uk/api/categories";
export const BRANDS_API_URL = "https://uktomars.co.uk/api/brands";
export const EVENTS_API_URL = "https://uktomars.co.uk/api/events";
export const QUOTE_API_URL = "https://uktomars.co.uk/api/quotes";

function formatQuoteErrorMessage(message, status) {
  const text = String(message || "").trim();
  if (!text) {
    return status === 401
      ? "Your session has expired. Please sign in again."
      : "Unable to submit quote request. Please try again.";
  }

  const lower = text.toLowerCase();
  if (
    lower.includes("smtp") ||
    lower.includes("authenticator") ||
    lower.includes("535 5.7.8") ||
    lower.includes("mailer")
  ) {
    return (
      "Your quote could not be completed because the server email account " +
      "(sales@uktomars.co.uk) is not configured correctly. " +
      "Please contact sales@uktomars.co.uk with your basket details, " +
      "or ask your site administrator to update the API mail settings."
    );
  }

  return text;
}

export async function submitQuote({ note = "", lines }) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error(
      "Please sign in to your trade account to request a quote.",
    );
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const response = await fetch(QUOTE_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ note, lines }),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const rawMessage = payload?.message || payload?.error || "";
    const message = formatQuoteErrorMessage(rawMessage, response.status);
    const error = new Error(message);
    error.status = response.status;
    error.rawMessage = rawMessage;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return payload;
}

export async function fetchQuotes({ page = 1 } = {}) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error("Please sign in to view your quote history.");
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const url =
    page > 1
      ? `${QUOTE_API_URL}?page=${encodeURIComponent(page)}`
      : QUOTE_API_URL;

  const response = await fetch(url, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      payload?.message ||
      payload?.error ||
      (response.status === 401
        ? "Your session has expired. Please sign in again."
        : "Unable to load quote history. Please try again.");
    const error = new Error(message);
    error.status = response.status;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return payload;
}

export async function fetchAllUserQuotes() {
  const all = [];
  let page = 1;
  let lastPage = 1;

  while (page <= lastPage) {
    const payload = await fetchQuotes({ page });
    const data = Array.isArray(payload?.data) ? payload.data : [];
    all.push(...data);
    lastPage = payload?.meta?.last_page ?? 1;
    page += 1;
  }

  return all.map(normalizeQuoteLines);
}

export async function fetchQuote(quoteId) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error("Please sign in to view this quote.");
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const id = String(quoteId ?? "").trim();
  if (!id) {
    throw new Error("Quote not found.");
  }

  const response = await fetch(`${QUOTE_API_URL}/${encodeURIComponent(id)}`, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      payload?.message ||
      payload?.error ||
      (response.status === 404
        ? "Quote not found."
        : response.status === 401
          ? "Your session has expired. Please sign in again."
          : "Unable to load quote details. Please try again.");
    const error = new Error(message);
    error.status = response.status;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return normalizeQuoteLines(payload?.data ?? payload);
}

export function getActiveQuoteLines(lines) {
  return (Array.isArray(lines) ? lines : []).filter(
    (line) => (Number(line?.quantity) || 0) > 0,
  );
}

export function normalizeQuoteLines(quote) {
  if (!quote) return quote;
  return {
    ...quote,
    lines: getActiveQuoteLines(quote.lines),
  };
}

function quoteAuthHeaders(token) {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
}

function formatQuoteUpdateError(payload, response) {
  const rawMessage = payload?.message || payload?.error || "";
  if (response.status === 405 || response.status === 404) {
    return (
      rawMessage ||
      "Quote editing is not available on the server yet. Please ask your administrator to enable quote line update routes."
    );
  }
  if (response.status === 403) {
    return rawMessage || "This quote can no longer be edited.";
  }
  if (response.status === 401) {
    return rawMessage || "Your session has expired. Please sign in again.";
  }
  return (
    formatQuoteErrorMessage(rawMessage, response.status) ||
    "Unable to update quote line. Please try again."
  );
}

function normalizeQuoteLineId(lineId) {
  const id = Number(lineId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("Quote line not found.");
  }
  return id;
}

function buildQuoteLinesPayload(lines, { allowZeroQuantity = false } = {}) {
  const normalized = (Array.isArray(lines) ? lines : [])
    .map((line) => ({
      id: line?.id,
      quantity: Number(line?.quantity) || 0,
    }))
    .filter((line) => line.id != null);

  if (allowZeroQuantity) {
    return normalized;
  }

  return normalized.filter((line) => line.quantity > 0);
}

export async function updateQuote(
  quoteId,
  { lines, note, allowZeroQuantity = false } = {},
) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error("Please sign in to update this quote.");
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const id = String(quoteId ?? "").trim();
  if (!id) {
    throw new Error("Quote not found.");
  }

  const body = {};
  if (lines != null) {
    body.lines = buildQuoteLinesPayload(lines, { allowZeroQuantity });
  }
  if (note !== undefined) {
    body.note = note;
  }

  const response = await fetch(`${QUOTE_API_URL}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: quoteAuthHeaders(token),
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = formatQuoteUpdateError(payload, response);
    const error = new Error(message);
    error.status = response.status;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return normalizeQuoteLines(payload?.data ?? payload);
}

export async function updateQuoteLineQuantity(quoteId, lineId, quantity) {
  const nextQuantity = Number(quantity);
  if (!Number.isFinite(nextQuantity) || nextQuantity < 1) {
    throw new Error("Quantity must be at least 1.");
  }

  const quote = await fetchQuote(quoteId);
  const lines = (Array.isArray(quote?.lines) ? quote.lines : []).map((line) =>
    line.id === lineId ? { ...line, quantity: nextQuantity } : line,
  );

  return updateQuote(quoteId, { lines });
}

function isQuantityMinValidationError(error) {
  const message = String(error?.message || "").toLowerCase();
  return message.includes("at least 1") || message.includes("minimum");
}

export async function deleteQuoteLine(quoteId, lineId) {
  const normalizedLineId = normalizeQuoteLineId(lineId);

  try {
    return await updateQuote(quoteId, {
      lines: [{ id: normalizedLineId, quantity: 0 }],
      allowZeroQuantity: true,
    });
  } catch (error) {
    if (!isQuantityMinValidationError(error)) {
      throw error;
    }

    const quote = await fetchQuote(quoteId);
    const lines = getActiveQuoteLines(quote?.lines).filter(
      (line) => Number(line.id) !== normalizedLineId,
    );

    return updateQuote(quoteId, { lines });
  }
}

export async function fetchPendingQuotes() {
  const quotes = await fetchAllUserQuotes();
  return quotes.filter(
    (quote) =>
      String(quote?.status ?? "")
        .toLowerCase()
        .trim() === "pending",
  );
}

export async function fetchAllPaginated(url) {
  const all = [];
  let nextUrl = url;

  while (nextUrl) {
    const response = await fetch(nextUrl);
    if (!response.ok) throw new Error(`Failed to fetch ${nextUrl}`);

    const payload = await response.json();
    const data = Array.isArray(payload?.data) ? payload.data : [];
    all.push(...data);
    nextUrl = payload?.links?.next ?? null;
  }

  return all;
}

export const getProductsByBrandUrl = (brandId) => {
  const id = String(brandId ?? "").trim();
  if (!id) return PRODUCTS_API_URL;
  return `${PRODUCTS_API_URL}?brand_id=${encodeURIComponent(id)}`;
};

export const fetchProductsByBrand = (brandId) =>
  fetchAllPaginated(getProductsByBrandUrl(brandId));

export const findBrandById = async (brandId) => {
  const id = String(brandId ?? "").trim();
  if (!id) return null;

  const brands = await fetchAllPaginated(BRANDS_API_URL);
  return brands.find((brand) => String(brand?.id) === id) ?? null;
};

export const getBrandProductsPath = (brand) => {
  if (brand?.id == null) return "/Brands";
  const id = encodeURIComponent(String(brand.id));
  return `/Brands/${id}/products?brand_id=${id}`;
};

export const getProductsListUrl = ({
  category = "",
  subcategory = "",
} = {}) => {
  const params = new URLSearchParams();
  if (subcategory) {
    params.set("category", subcategory);
  } else if (category) {
    params.set("category", category);
  }

  const query = params.toString();
  return query ? `${PRODUCTS_API_URL}?${query}` : PRODUCTS_API_URL;
};

export const getCategoryProductsPath = (categorySlug) =>
  `/products?category=${encodeURIComponent(categorySlug)}`;
